import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Spin,
  Button,
  Tag,
  Descriptions,
  Empty,
  Space,
  Tooltip,
  Modal,
  Form,
  Input,
  Select,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Project, GateSummary, Template } from '../types';
import { useLookups } from '../context/LookupsContext';
import TeamAssignments from '../components/common/TeamAssignments';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { getGateName } = useLookups();

  const [project, setProject] = useState<Project | null>(null);
  const [gateSummaries, setGateSummaries] = useState<Map<string, GateSummary[]>>(new Map());
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [projectData, templatesData] = await Promise.all([
        api.getProject(projectId),
        api.getTemplates(),
      ]);
      setProject(projectData);
      setTemplates(templatesData);

      // Load gate summaries for each checklist
      const summaryMap = new Map<string, GateSummary[]>();
      if (projectData.checklists) {
        for (const checklist of projectData.checklists) {
          try {
            const summary = await api.getGateSummary(checklist.id);
            summaryMap.set(checklist.id, summary);
          } catch (err) {
            console.error(`Failed to load summary for checklist ${checklist.id}:`, err);
          }
        }
      }
      setGateSummaries(summaryMap);
    } catch (error) {
      message.error('Failed to load project');
      console.error('Failed to load project:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const getOverallProgress = (summary: GateSummary[]): number => {
    if (!summary || summary.length === 0) return 0;
    const total = summary.reduce((acc, gate) => acc + gate.total, 0);
    const completed = summary.reduce((acc, gate) => acc + gate.completed + gate.naCount, 0);
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getGateRYG = (gate: GateSummary): 'red' | 'yellow' | 'green' => {
    if (gate.completionPercentage === 100) return 'green';
    if (gate.inProgress > 0 || gate.completionPercentage > 0) return 'yellow';
    return 'red';
  };

  const getRYGColor = (ryg: 'red' | 'yellow' | 'green'): string => {
    switch (ryg) {
      case 'green':
        return '#52c41a';
      case 'yellow':
        return '#faad14';
      case 'red':
        return '#ff4d4f';
    }
  };

  const handleCreateChecklist = async () => {
    try {
      const values = await form.validateFields();
      await api.createChecklist({
        projectId: projectId!,
        templateId: values.templateId,
        name: values.name,
      });
      message.success('Checklist created');
      setCreateModalVisible(false);
      form.resetFields();
      loadProject();
    } catch (error) {
      message.error('Failed to create checklist');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!project) {
    return <Empty description="Project not found" />;
  }

  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/projects')}
        style={{ marginBottom: 16 }}
      >
        Back to Projects
      </Button>

      <Card>
        <Row gutter={24}>
          <Col flex="auto">
            <Title level={3} style={{ margin: 0 }}>
              {project.name}
            </Title>
            {project.description && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {project.description}
              </Text>
            )}
          </Col>
          <Col>
            <Tag color={project.status === 'active' ? 'green' : 'default'}>
              {project.status?.toUpperCase()}
            </Tag>
          </Col>
        </Row>

        <Descriptions style={{ marginTop: 24 }} column={{ xs: 1, sm: 2, md: 4 }}>
          {project.address && <Descriptions.Item label="Address">{project.address}</Descriptions.Item>}
          {project.city && <Descriptions.Item label="City">{project.city}</Descriptions.Item>}
          {project.state && <Descriptions.Item label="State">{project.state}</Descriptions.Item>}
          <Descriptions.Item label="Created">
            {dayjs(project.createdAt).format('MMM D, YYYY')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <TeamAssignments
        projectId={projectId!}
        teamAssignments={project.teamAssignments}
        onUpdate={loadProject}
      />

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            Checklists
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
            Add Checklist
          </Button>
        </div>

        {(!project.checklists || project.checklists.length === 0) ? (
          <Card>
            <Empty description="No checklists yet">
              <Button type="primary" onClick={() => setCreateModalVisible(true)}>
                Create Checklist
              </Button>
            </Empty>
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {project.checklists.map((checklist) => {
              const summary = gateSummaries.get(checklist.id) || [];
              const progress = getOverallProgress(summary);

              return (
                <Col xs={24} key={checklist.id}>
                  <Card
                    title={
                      <Space>
                        <span>{checklist.name}</span>
                        <Tag color="blue">Gate {checklist.currentGate}</Tag>
                      </Space>
                    }
                    extra={
                      <Button
                        type="primary"
                        onClick={() => navigate(`/projects/${project.id}/checklist/${checklist.id}`)}
                      >
                        View Checklist
                      </Button>
                    }
                  >
                    <Row gutter={16} align="middle">
                      <Col xs={24} md={6}>
                        <div style={{ textAlign: 'center' }}>
                          <Progress
                            type="circle"
                            percent={progress}
                            size={100}
                            strokeColor={{
                              '0%': '#1c7a92',
                              '100%': '#52c41a',
                            }}
                          />
                          <div style={{ marginTop: 8 }}>
                            <Text strong>Overall Progress</Text>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} md={18}>
                        <Title level={5}>Gate Progress</Title>
                        <Row gutter={[8, 8]}>
                          {summary.map((gate) => {
                            const ryg = getGateRYG(gate);
                            return (
                              <Col xs={12} sm={8} md={6} lg={4} key={gate.gateNumber}>
                                <Tooltip
                                  title={
                                    <div>
                                      <div><strong>{gate.gateName || getGateName(gate.gateNumber)}</strong></div>
                                      <div>Completed: {gate.completed}</div>
                                      <div>In Progress: {gate.inProgress}</div>
                                      <div>Not Started: {gate.notStarted}</div>
                                      <div>N/A: {gate.naCount}</div>
                                    </div>
                                  }
                                >
                                  <Card
                                    size="small"
                                    style={{
                                      textAlign: 'center',
                                      borderColor: getRYGColor(ryg),
                                      cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                      navigate(
                                        `/projects/${project.id}/checklist/${checklist.id}?gate=${gate.gateNumber}`
                                      )
                                    }
                                  >
                                    <div
                                      style={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: getRYGColor(ryg),
                                        margin: '0 auto 4px',
                                      }}
                                    />
                                    <Text strong>G{gate.gateNumber}</Text>
                                    <div>
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        {gate.completionPercentage}%
                                      </Text>
                                    </div>
                                  </Card>
                                </Tooltip>
                              </Col>
                            );
                          })}
                        </Row>

                        <Row gutter={16} style={{ marginTop: 16 }}>
                          <Col>
                            <Space>
                              <CheckCircleOutlined style={{ color: '#52c41a' }} />
                              <Text>
                                {summary.reduce((acc, g) => acc + g.completed, 0)} Completed
                              </Text>
                            </Space>
                          </Col>
                          <Col>
                            <Space>
                              <ClockCircleOutlined style={{ color: '#faad14' }} />
                              <Text>
                                {summary.reduce((acc, g) => acc + g.inProgress, 0)} In Progress
                              </Text>
                            </Space>
                          </Col>
                          <Col>
                            <Space>
                              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                              <Text>
                                {summary.reduce((acc, g) => acc + g.notStarted, 0)} Not Started
                              </Text>
                            </Space>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      <Modal
        title="Create Checklist"
        open={createModalVisible}
        onOk={handleCreateChecklist}
        onCancel={() => setCreateModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Checklist Name"
            rules={[{ required: true, message: 'Please enter checklist name' }]}
            initialValue={`${project.name} - Due Diligence`}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="templateId"
            label="Template"
            rules={[{ required: true, message: 'Please select a template' }]}
          >
            <Select placeholder="Select template">
              {templates.map((template) => (
                <Select.Option key={template.id} value={template.id}>
                  {template.name} ({template.itemCount || 290} items)
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectDetail;
